/**
 * Litigations panel -- embedded inside a Task's detail view as the "Litigations"
 * tab. Lists every Litigation under that task, lets the user manage steps
 * (replies / hearings / appeals / orders) and raise invoices either per-step
 * or at the parent-litigation level. Supports documents, stage tracking,
 * dormancy/pause, limitation countdowns, and quick outcome logging.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getLitigationsForTask,
  createLitigation,
  updateLitigation,
  deleteLitigation,
  addLitigationStep,
  updateLitigationStep,
  deleteLitigationStep,
  uploadLitigationDocument,
  deleteLitigationDocument,
  uploadsUrl,
  getStaff,
} from '../api';
import { showToast } from '../utils/toast';
import { formatDateDdMmmYy, formatRupeesUI } from '../utils/format';

// ---- Types ------------------------------------------------------------------

type LitStatus = 'OPEN' | 'CLOSED' | 'WON' | 'LOST' | 'PARTIALLY_WON' | 'SETTLED' | 'WITHDRAWN';
type Stage = 'ASSESSMENT' | 'FIRST_APPEAL' | 'TRIBUNAL' | 'HIGH_COURT' | 'SUPREME_COURT' | 'OTHER';
type StepType = 'REPLY' | 'HEARING' | 'APPEAL' | 'ORDER_RECEIVED' | 'NOTICE_RECEIVED' | 'OTHER';
type StepStatus = 'PENDING' | 'COMPLETED' | 'ADJOURNED' | 'SKIPPED';
type PausedReason = 'HEARING_AWAITED' | 'ORDER_AWAITED' | 'STAY_GRANTED' | 'OTHER';
type LawArea = 'INCOME_TAX' | 'GST' | 'OTHER';
type DocDirection = 'FILED' | 'RECEIVED';

interface InvoiceLite {
  id: number; invoiceNumber: string; status: string;
  totalAmount?: number | string | null;
  amount?: number | string | null;
}

interface LitDocument {
  id: number;
  litigationId: number;
  stepId: number | null;
  direction: DocDirection;
  title: string | null;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  uploadedById: number;
  createdAt: string;
}

interface Step {
  id: number;
  type: StepType;
  title: string;
  description: string | null;
  dueDate: string | null;
  hearingDate: string | null;
  status: StepStatus;
  completedOn: string | null;
  outcome: string | null;
  billableAmount: number | string | null;
  assignedTo: { id: number; staffName: string } | null;
  invoice: InvoiceLite | null;
  stage: Stage | null;
  documents: Array<{ id: number }>;
}

interface Litigation {
  id: number;
  litigationNumber: string;
  title: string;
  caseNumber: string | null;
  forum: string | null;
  opposingParty: string | null;
  ourReference: string | null;
  status: LitStatus;
  startedOn: string | null;
  closedOn: string | null;
  outcome: string | null;
  notes: string | null;
  currentStage: Stage | null;
  lawArea: LawArea | null;
  responsible: { id: number; staffName: string } | null;
  responsibleId: number | null;
  pausedReason: PausedReason | null;
  pausedOn: string | null;
  pausedNote: string | null;
  resumeOn: string | null;
  limitationDate: string | null;
  limitationBasis: string | null;
  disputedAmount: number | string | null;
  preDepositPaid: number | string | null;
  preDepositRef: string | null;
  hasNextAction: boolean;
  lastActivityAt: string | null;
  fees: {
    quoted: number;
    billed: number;
    collected: number;
    outstandingAR: number;
    unbilledQuote: number;
    unbilledCompleted: number;
  };
  steps: Step[];
  invoices: InvoiceLite[];
  documents: LitDocument[];
}

interface StaffLite { id: number; staffName: string; isActive?: boolean }

// ---- Constants --------------------------------------------------------------

const STATUS_OPTIONS: LitStatus[] = ['OPEN','CLOSED','WON','LOST','PARTIALLY_WON','SETTLED','WITHDRAWN'];
const STAGE_OPTIONS: Stage[] = ['ASSESSMENT','FIRST_APPEAL','TRIBUNAL','HIGH_COURT','SUPREME_COURT','OTHER'];
const LAW_AREA_OPTIONS: { value: LawArea; label: string }[] = [
  { value: 'INCOME_TAX', label: 'Income Tax' },
  { value: 'GST', label: 'GST' },
  { value: 'OTHER', label: 'Other' },
];
const STEP_TYPE_OPTIONS: { value: StepType; label: string }[] = [
  { value: 'REPLY',           label: 'Reply / Submission' },
  { value: 'HEARING',         label: 'Hearing' },
  { value: 'APPEAL',          label: 'Appeal' },
  { value: 'ORDER_RECEIVED',  label: 'Order Received' },
  { value: 'NOTICE_RECEIVED', label: 'Notice Received' },
  { value: 'OTHER',           label: 'Other' },
];
const STEP_STATUS_OPTIONS: StepStatus[] = ['PENDING','COMPLETED','ADJOURNED','SKIPPED'];
const PAUSED_REASON_OPTIONS: { value: PausedReason; label: string }[] = [
  { value: 'HEARING_AWAITED', label: 'Hearing awaited' },
  { value: 'ORDER_AWAITED', label: 'Order awaited' },
  { value: 'STAY_GRANTED', label: 'Stay granted' },
  { value: 'OTHER', label: 'Other' },
];

const STATUS_BADGE: Record<LitStatus, string> = {
  OPEN:          'bg-blue-100 text-blue-700 border-blue-200',
  CLOSED:        'bg-gray-100 text-gray-700 border-gray-200',
  WON:           'bg-emerald-100 text-emerald-700 border-emerald-200',
  PARTIALLY_WON: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  LOST:          'bg-red-100 text-red-700 border-red-200',
  SETTLED:       'bg-indigo-100 text-indigo-700 border-indigo-200',
  WITHDRAWN:     'bg-amber-100 text-amber-700 border-amber-200',
};

const STAGE_BADGE: Record<Stage, string> = {
  ASSESSMENT:    'bg-orange-100 text-orange-700 border-orange-200',
  FIRST_APPEAL:  'bg-blue-100 text-blue-700 border-blue-200',
  TRIBUNAL:      'bg-purple-100 text-purple-700 border-purple-200',
  HIGH_COURT:    'bg-indigo-100 text-indigo-700 border-indigo-200',
  SUPREME_COURT: 'bg-red-100 text-red-700 border-red-200',
  OTHER:         'bg-gray-100 text-gray-700 border-gray-200',
};

const STEP_STATUS_BADGE: Record<StepStatus, string> = {
  PENDING:   'bg-amber-50 text-amber-700 border-amber-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ADJOURNED: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  SKIPPED:   'bg-gray-50 text-gray-500 border-gray-200',
};

const STEP_TYPE_ICON: Record<StepType, string> = {
  REPLY: '✍️', HEARING: '⚖️', APPEAL: '📤', ORDER_RECEIVED: '📜', NOTICE_RECEIVED: '📨', OTHER: '•',
};

const LIMITATION_PRESETS: Record<string, Record<string, number>> = {
  INCOME_TAX: { FIRST_APPEAL: 30, TRIBUNAL: 60, HIGH_COURT: 120, SUPREME_COURT: 90 },
  GST: { FIRST_APPEAL: 90, TRIBUNAL: 90 },
};

const TEMPLATE_OPTIONS = [
  { key: '', label: '— None —' },
  { key: 'GST_SCN_73_74', label: 'GST SCN u/s 73/74' },
  { key: 'IT_SCRUTINY', label: 'IT Scrutiny' },
  { key: 'IT_FIRST_APPEAL', label: 'IT First Appeal' },
  { key: 'GST_FIRST_APPEAL', label: 'GST First Appeal' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const fmtStage = (s: Stage) => s.replace(/_/g, ' ');
const fmtAmt = (v: number | string | null | undefined) => {
  if (v == null || v === '' || Number(v) === 0) return null;
  return `₹${formatRupeesUI(v as any)}`;
};

// ---- Panel ------------------------------------------------------------------

const LitigationsPanel: React.FC<{ taskId: number; canEdit?: boolean }> = ({ taskId, canEdit = true }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Litigation[]>([]);
  const [staff, setStaff] = useState<StaffLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [editing, setEditing] = useState<Litigation | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getLitigationsForTask(taskId);
      setItems(r.data);
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed to load litigations', 'error');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    getStaff().then((r) => setStaff((r.data as StaffLite[]).filter((s) => s.isActive !== false))).catch(() => {});
  }, []);

  // Aggregated fees across all litigations
  const totals = useMemo(() => {
    const sums = { quoted: 0, billed: 0, collected: 0, outstandingAR: 0 };
    for (const lit of items) {
      sums.quoted += lit.fees?.quoted ?? 0;
      sums.billed += lit.fees?.billed ?? 0;
      sums.collected += lit.fees?.collected ?? 0;
      sums.outstandingAR += lit.fees?.outstandingAR ?? 0;
    }
    return sums;
  }, [items]);
  const showTotals = items.length > 0 && (totals.quoted > 0 || totals.billed > 0 || totals.collected > 0 || totals.outstandingAR > 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-800">Litigations</h3>
          <p className="text-xs text-gray-500 mt-0.5">Legal matters under this task -- replies, hearings, appeals, orders. Each step can be invoiced separately.</p>
        </div>
        {canEdit && (
          <button onClick={() => { setEditing(null); setCreatorOpen(true); }} className="btn-primary text-sm">
            + New Litigation
          </button>
        )}
      </div>

      {/* Totals strip */}
      {showTotals && (
        <div className="flex flex-wrap gap-4 px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg text-[11px] text-gray-700">
          <span>Quoted: <b className="text-gray-900">{fmtAmt(totals.quoted) ?? '—'}</b></span>
          <span>Billed: <b className="text-emerald-700">{fmtAmt(totals.billed) ?? '—'}</b></span>
          <span>Collected: <b className="text-blue-700">{fmtAmt(totals.collected) ?? '—'}</b></span>
          <span>O/s AR: <b className="text-amber-700">{fmtAmt(totals.outstandingAR) ?? '—'}</b></span>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-400 text-center py-8">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-8 border border-dashed border-gray-200 rounded-lg">
          No litigations yet for this task.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((lit) => (
            <LitigationCard
              key={lit.id}
              litigation={lit}
              staff={staff}
              canEdit={canEdit}
              onEdit={() => { setEditing(lit); setCreatorOpen(true); }}
              onDelete={async () => {
                if (!window.confirm(`Delete litigation "${lit.title}"?\n\nSteps under it will also be removed. Past invoices stay.`)) return;
                try { await deleteLitigation(lit.id); showToast('Litigation deleted', 'success'); refresh(); }
                catch (err: any) { showToast(err?.response?.data?.message ?? 'Delete failed', 'error'); }
              }}
              onInvoice={() => navigate(`/invoices?taskId=${taskId}&litigationId=${lit.id}`)}
              onRefresh={refresh}
              onAddStep={async (data) => {
                try { await addLitigationStep(lit.id, data); showToast('Step added', 'success'); refresh(); }
                catch (err: any) { showToast(err?.response?.data?.message ?? 'Add failed', 'error'); }
              }}
              onUpdateStep={async (stepId, data) => {
                try { await updateLitigationStep(stepId, data); showToast('Step updated', 'success'); refresh(); }
                catch (err: any) { showToast(err?.response?.data?.message ?? 'Update failed', 'error'); }
              }}
              onDeleteStep={async (stepId) => {
                if (!window.confirm('Delete this step?')) return;
                try { await deleteLitigationStep(stepId); showToast('Step deleted', 'success'); refresh(); }
                catch (err: any) { showToast(err?.response?.data?.message ?? 'Delete failed', 'error'); }
              }}
              onInvoiceStep={(stepId) => navigate(`/invoices?taskId=${taskId}&litigationStepId=${stepId}`)}
            />
          ))}
        </div>
      )}

      {creatorOpen && (
        <LitigationEditor
          existing={editing}
          staff={staff}
          onClose={() => { setEditing(null); setCreatorOpen(false); }}
          onSaved={() => { setEditing(null); setCreatorOpen(false); refresh(); }}
          onSubmit={async (data) => {
            if (editing) await updateLitigation(editing.id, data);
            else await createLitigation(taskId, data);
          }}
        />
      )}
    </div>
  );
};

// ---- Litigation card with steps inline --------------------------------------

const LitigationCard: React.FC<{
  litigation: Litigation;
  staff: StaffLite[];
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onInvoice: () => void;
  onRefresh: () => void;
  onAddStep: (data: any) => Promise<void>;
  onUpdateStep: (stepId: number, data: any) => Promise<void>;
  onDeleteStep: (stepId: number) => Promise<void>;
  onInvoiceStep: (stepId: number) => void;
}> = ({ litigation, staff, canEdit, onEdit, onDelete, onInvoice, onRefresh, onAddStep, onUpdateStep, onDeleteStep, onInvoiceStep }) => {
  const [stepFormOpen, setStepFormOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<Step | null>(null);
  const [logOutcomeStep, setLogOutcomeStep] = useState<Step | null>(null);
  const [moveStageOpen, setMoveStageOpen] = useState(false);

  const fees = litigation.fees;

  // Limitation countdown
  const limitChip = useMemo(() => {
    if (!litigation.limitationDate || litigation.status !== 'OPEN') return null;
    const today = new Date(todayISO());
    const limDate = new Date(litigation.limitationDate);
    const diffMs = limDate.getTime() - today.getTime();
    const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    let color = 'bg-gray-100 text-gray-600 border-gray-200';
    if (daysLeft < 7) color = 'bg-red-100 text-red-700 border-red-200';
    else if (daysLeft < 15) color = 'bg-amber-100 text-amber-700 border-amber-200';
    return { daysLeft, color };
  }, [litigation.limitationDate, litigation.status]);

  // Group steps by stage
  const stageGroups = useMemo(() => {
    const stageOrder: (Stage | null)[] = [...STAGE_OPTIONS].reverse();
    const groups: { stage: Stage | null; label: string; steps: Step[] }[] = [];
    const byStage = new Map<Stage | null, Step[]>();
    for (const step of litigation.steps) {
      const key = step.stage ?? null;
      if (!byStage.has(key)) byStage.set(key, []);
      byStage.get(key)!.push(step);
    }
    // Add groups in stage order (highest first)
    for (const stage of stageOrder) {
      if (byStage.has(stage)) {
        groups.push({ stage, label: fmtStage(stage!), steps: byStage.get(stage)! });
        byStage.delete(stage);
      }
    }
    // Null-stage group at the bottom
    if (byStage.has(null)) {
      groups.push({ stage: null, label: 'Unassigned', steps: byStage.get(null)! });
    }
    return groups;
  }, [litigation.steps]);

  // Pause / Resume handler
  const handlePauseResume = async () => {
    try {
      if (litigation.pausedReason) {
        await updateLitigation(litigation.id, { pausedReason: null, pausedOn: null, pausedNote: null, resumeOn: null });
        showToast('Litigation resumed', 'success');
      } else {
        await updateLitigation(litigation.id, { pausedReason: 'OTHER', pausedOn: todayISO() });
        showToast('Litigation paused', 'success');
      }
      onRefresh();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed', 'error');
    }
  };

  // Move stage handler
  const handleMoveStage = async (data: { currentStage: Stage; createAppealStep: boolean; appealDueDate: string | null }) => {
    try {
      await updateLitigation(litigation.id, { currentStage: data.currentStage });
      if (data.createAppealStep) {
        await addLitigationStep(litigation.id, {
          type: 'APPEAL' as StepType,
          title: `Appeal to ${fmtStage(data.currentStage)}`,
          dueDate: data.appealDueDate || null,
          status: 'PENDING' as StepStatus,
          stage: data.currentStage,
        });
      }
      showToast('Stage updated', 'success');
      onRefresh();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed to move stage', 'error');
    }
  };

  // Log outcome handler
  const handleLogOutcome = async (stepUpdate: any, extraActions?: any) => {
    try {
      await onUpdateStep(logOutcomeStep!.id, stepUpdate);
      if (extraActions?.createStep) {
        await onAddStep(extraActions.createStep);
      }
      if (extraActions?.pauseLitigation) {
        await updateLitigation(litigation.id, {
          pausedReason: extraActions.pauseLitigation.pausedReason,
          pausedOn: todayISO(),
        });
      }
      onRefresh();
    } catch {
      // errors already toasted in parent callbacks
    }
  };

  // Document upload handler
  const handleDocUpload = async (file: File, direction: DocDirection, title: string, stepId: number | null) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (stepId) fd.append('stepId', String(stepId));
      fd.append('direction', direction);
      if (title) fd.append('title', title);
      await uploadLitigationDocument(litigation.id, fd);
      showToast('Document uploaded', 'success');
      onRefresh();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Upload failed', 'error');
    }
  };

  const handleDocDelete = async (docId: number) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteLitigationDocument(docId);
      showToast('Document deleted', 'success');
      onRefresh();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Delete failed', 'error');
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-200">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-purple-700 font-bold">{litigation.litigationNumber}</span>
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_BADGE[litigation.status]}`}>{litigation.status.replace(/_/g, ' ')}</span>
              {litigation.currentStage && (
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STAGE_BADGE[litigation.currentStage]}`}>{fmtStage(litigation.currentStage)}</span>
              )}
              {litigation.forum && <span className="text-[11px] text-gray-500">{'⚖'} {litigation.forum}</span>}
              {litigation.responsible && <span className="text-[11px] text-gray-500">{'👤'} {litigation.responsible.staffName}</span>}
              {limitChip && (
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${limitChip.color}`}>
                  Appeal: {limitChip.daysLeft}d left
                </span>
              )}
            </div>
            <div className="text-sm font-semibold text-gray-900 mt-0.5 truncate" title={litigation.title}>{litigation.title}</div>
            <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-3">
              {litigation.caseNumber && <span>Case: <span className="font-mono">{litigation.caseNumber}</span></span>}
              {litigation.opposingParty && <span>vs {litigation.opposingParty}</span>}
              {litigation.startedOn && <span>Started {formatDateDdMmmYy(litigation.startedOn)}</span>}
              {litigation.closedOn && <span>Closed {formatDateDdMmmYy(litigation.closedOn)}</span>}
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
              <button onClick={onInvoice} className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200" title="Raise a parent-level / final settlement invoice for this litigation">+ Invoice</button>
              <button onClick={() => setMoveStageOpen(true)} className="text-xs px-2 py-1 rounded bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200">Move stage...</button>
              <button onClick={handlePauseResume} className="text-xs px-2 py-1 rounded bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200">
                {litigation.pausedReason ? 'Resume' : 'Pause'}
              </button>
              <button onClick={onEdit} className="text-xs text-gray-500 hover:text-blue-600 px-2">Edit</button>
              <button onClick={onDelete} className="text-xs text-gray-500 hover:text-red-600 px-2">Delete</button>
            </div>
          )}
        </div>
        {litigation.outcome && (
          <p className="text-xs text-gray-700 mt-2 italic">Outcome: {litigation.outcome}</p>
        )}
      </div>

      {/* Dormant banner */}
      {litigation.pausedReason && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-sm text-yellow-800">
          {'⏸'} Dormant -- {PAUSED_REASON_OPTIONS.find((o) => o.value === litigation.pausedReason)?.label ?? litigation.pausedReason}
          {litigation.pausedNote && <span className="text-yellow-600 ml-2">({litigation.pausedNote})</span>}
          {litigation.resumeOn && <span className="ml-2">Resume: {formatDateDdMmmYy(litigation.resumeOn)}</span>}
        </div>
      )}

      {/* Disputed amount */}
      {litigation.disputedAmount != null && Number(litigation.disputedAmount) > 0 && (
        <div className="px-4 py-2 bg-red-50/50 border-b border-gray-200 text-xs text-gray-700">
          Disputed: <b>{fmtAmt(litigation.disputedAmount)}</b>
          {litigation.preDepositPaid != null && Number(litigation.preDepositPaid) > 0 && (
            <> | Pre-deposit: <b>{fmtAmt(litigation.preDepositPaid)}</b>
              {litigation.preDepositRef && <span className="text-gray-500"> (Ref: {litigation.preDepositRef})</span>}
            </>
          )}
        </div>
      )}

      {/* No next action warning */}
      {litigation.status === 'OPEN' && !litigation.hasNextAction && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 font-medium">
          {'⚠'} No next action scheduled -- this matter needs attention
        </div>
      )}

      {/* Step totals + parent invoices */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-[11px] text-gray-600 flex flex-wrap items-center gap-4">
        <span>Steps: <b className="text-gray-900">{litigation.steps.length}</b></span>
        <span>Quoted: <b className="text-gray-900">{fmtAmt(fees?.quoted) ?? '—'}</b></span>
        <span>Billed: <b className="text-emerald-700">{fmtAmt(fees?.billed) ?? '—'}</b></span>
        <span>Collected: <b className="text-blue-700">{fmtAmt(fees?.collected) ?? '—'}</b></span>
        {(fees?.outstandingAR ?? 0) > 0 && <span>O/s AR: <b className="text-amber-700">{fmtAmt(fees.outstandingAR)}</b></span>}
        {litigation.invoices.length > 0 && (
          <span className="ml-auto">
            Parent invoices:&nbsp;
            {litigation.invoices.map((inv) => (
              <span key={inv.id} className="font-mono text-blue-700 ml-1">{inv.invoiceNumber}</span>
            ))}
          </span>
        )}
      </div>

      {/* Steps table grouped by stage */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2 w-12">Type</th>
              <th className="text-left px-3 py-2">Step</th>
              <th className="text-left px-3 py-2 w-28">Due</th>
              <th className="text-left px-3 py-2 w-28">Hearing</th>
              <th className="text-left px-3 py-2 w-28">Status</th>
              <th className="text-left px-3 py-2 w-32">Assigned</th>
              <th className="text-right px-3 py-2 w-24">Billable</th>
              <th className="text-left px-3 py-2 w-40">Invoice</th>
              {canEdit && <th className="text-right px-3 py-2 w-36">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {litigation.steps.length === 0 ? (
              <tr><td colSpan={canEdit ? 9 : 8} className="text-center text-gray-400 py-4">No steps yet</td></tr>
            ) : stageGroups.map((group) => (
              <React.Fragment key={group.label}>
                {/* Stage group header */}
                <tr>
                  <td colSpan={canEdit ? 9 : 8} className="px-0 py-0">
                    <div className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide ${group.stage ? STAGE_BADGE[group.stage] : 'bg-gray-100 text-gray-500'}`}>
                      {group.label}
                    </div>
                  </td>
                </tr>
                {group.steps.map((step) => (
                  <tr key={step.id} className="border-t border-gray-100 hover:bg-gray-50/60 align-top">
                    <td className="px-3 py-2 text-center" title={STEP_TYPE_OPTIONS.find((o) => o.value === step.type)?.label || step.type}>
                      <span className="text-base">{STEP_TYPE_ICON[step.type]}</span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">
                        {step.title}
                        {step.documents && step.documents.length > 0 && (
                          <span className="text-[11px] text-gray-500 ml-1">{'📎'} {step.documents.length}</span>
                        )}
                      </div>
                      {step.description && <div className="text-[11px] text-gray-500 truncate max-w-[280px]" title={step.description}>{step.description}</div>}
                      {step.outcome && <div className="text-[11px] text-gray-700 italic mt-0.5">{'→'} {step.outcome}</div>}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{step.dueDate ? formatDateDdMmmYy(step.dueDate) : '—'}</td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{step.hearingDate ? formatDateDdMmmYy(step.hearingDate) : '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STEP_STATUS_BADGE[step.status]}`}>{step.status}</span>
                      {step.completedOn && <div className="text-[10px] text-gray-400 mt-0.5">on {formatDateDdMmmYy(step.completedOn)}</div>}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{step.assignedTo?.staffName ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {step.billableAmount != null && Number(step.billableAmount) > 0
                        ? `₹${formatRupeesUI(step.billableAmount as any)}`
                        : <span className="text-gray-300">{'—'}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {step.invoice ? (
                        <span className="text-[11px] font-mono text-blue-700">{step.invoice.invoiceNumber}<span className="ml-1 text-gray-400">({step.invoice.status})</span></span>
                      ) : (
                        canEdit && <button onClick={() => onInvoiceStep(step.id)} className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200">+ Invoice</button>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {step.status === 'PENDING' && (
                          <button onClick={() => setLogOutcomeStep(step)} className="text-[11px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 mr-1">Log outcome</button>
                        )}
                        <button onClick={() => { setEditingStep(step); setStepFormOpen(true); }} className="text-xs text-blue-600 hover:text-blue-800 px-1">Edit</button>
                        <button onClick={() => onDeleteStep(step.id)} className="text-xs text-red-500 hover:text-red-700 px-1">Del</button>
                      </td>
                    )}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
          <button onClick={() => { setEditingStep(null); setStepFormOpen(true); }} className="text-xs text-blue-600 hover:underline">
            + Add step (reply / hearing / appeal / order)
          </button>
        </div>
      )}

      {/* Documents section */}
      <DocumentsSection
        litigation={litigation}
        canEdit={canEdit}
        onUpload={handleDocUpload}
        onDelete={handleDocDelete}
      />

      {/* Modals */}
      {stepFormOpen && (
        <StepEditor
          existing={editingStep}
          staff={staff}
          defaultStage={litigation.currentStage}
          defaultAssigneeId={litigation.responsible?.id ?? null}
          onClose={() => { setStepFormOpen(false); setEditingStep(null); }}
          onSubmit={async (data) => {
            if (editingStep) await onUpdateStep(editingStep.id, data);
            else await onAddStep(data);
            setStepFormOpen(false);
            setEditingStep(null);
          }}
        />
      )}

      {logOutcomeStep && (
        <LogOutcomeModal
          step={logOutcomeStep}
          litigationId={litigation.id}
          litigation={litigation}
          onClose={() => setLogOutcomeStep(null)}
          onSave={async (stepUpdate, extraActions) => {
            await handleLogOutcome(stepUpdate, extraActions);
            setLogOutcomeStep(null);
          }}
        />
      )}

      {moveStageOpen && (
        <MoveStageModal
          litigation={litigation}
          onClose={() => setMoveStageOpen(false)}
          onSave={async (data) => {
            await handleMoveStage(data);
            setMoveStageOpen(false);
          }}
        />
      )}
    </div>
  );
};

// ---- Documents section ------------------------------------------------------

const DocumentsSection: React.FC<{
  litigation: Litigation;
  canEdit: boolean;
  onUpload: (file: File, direction: DocDirection, title: string, stepId: number | null) => Promise<void>;
  onDelete: (docId: number) => Promise<void>;
}> = ({ litigation, canEdit, onUpload, onDelete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [direction, setDirection] = useState<DocDirection>('FILED');
  const [title, setTitle] = useState('');
  const [stepId, setStepId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const filedDocs = (litigation.documents ?? []).filter((d) => d.direction === 'FILED');
  const receivedDocs = (litigation.documents ?? []).filter((d) => d.direction === 'RECEIVED');

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file, direction, title.trim(), stepId);
      setFile(null);
      setTitle('');
      setStepId(null);
    } finally {
      setUploading(false);
    }
  };

  const findStepTitle = (sid: number | null) => {
    if (!sid) return null;
    const step = litigation.steps.find((s) => s.id === sid);
    return step?.title ?? null;
  };

  const renderDoc = (doc: LitDocument) => (
    <div key={doc.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-gray-800 font-medium flex-1 min-w-0 truncate">{doc.title || doc.originalName}</span>
      {doc.stepId && (
        <span className="text-[10px] text-gray-400 flex-shrink-0">Step: {findStepTitle(doc.stepId) ?? `#${doc.stepId}`}</span>
      )}
      <span className="text-[10px] text-gray-400 flex-shrink-0">{formatDateDdMmmYy(doc.createdAt)}</span>
      <a href={uploadsUrl(doc.fileName)} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-600 hover:text-blue-800 flex-shrink-0">Download</a>
      {canEdit && (
        <button onClick={() => onDelete(doc.id)} className="text-[11px] text-red-500 hover:text-red-700 flex-shrink-0">Del</button>
      )}
    </div>
  );

  const hasDocs = filedDocs.length > 0 || receivedDocs.length > 0;

  return (
    <div className="px-4 py-3 border-t border-gray-200">
      <h4 className="text-xs font-semibold text-gray-700 mb-2">Documents</h4>

      {!hasDocs && !canEdit && (
        <p className="text-xs text-gray-400">No documents.</p>
      )}

      {filedDocs.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Filed by us</div>
          {filedDocs.map(renderDoc)}
        </div>
      )}

      {receivedDocs.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Received from dept</div>
          {receivedDocs.map(renderDoc)}
        </div>
      )}

      {canEdit && (
        <div className="mt-2 flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[10px] text-gray-500">File</label>
            <input
              type="file"
              className="block text-xs"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">Direction</label>
            <select className="input-field text-xs py-1" value={direction} onChange={(e) => setDirection(e.target.value as DocDirection)}>
              <option value="FILED">Filed by us</option>
              <option value="RECEIVED">Received</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500">Title (optional)</label>
            <input className="input-field text-xs py-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Document title" />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">Step (optional)</label>
            <select className="input-field text-xs py-1" value={stepId ?? ''} onChange={(e) => setStepId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">-- None --</option>
              {litigation.steps.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>
          <button onClick={handleUpload} disabled={!file || uploading} className="btn-primary text-xs py-1 px-3">
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      )}
    </div>
  );
};

// ---- Log Outcome modal ------------------------------------------------------

const LogOutcomeModal: React.FC<{
  step: Step;
  litigationId: number;
  litigation: Litigation;
  onClose: () => void;
  onSave: (stepUpdate: any, extraActions?: any) => Promise<void>;
}> = ({ step, litigation, onClose, onSave }) => {
  type Preset = 'adjourned' | 'order_reserved' | 'submissions_completed' | 'order_received' | null;
  const [preset, setPreset] = useState<Preset>(null);
  const [nextDate, setNextDate] = useState('');
  const [pauseAsAwaited, setPauseAsAwaited] = useState(false);
  const [outcomeText, setOutcomeText] = useState('');
  const [completedOn, setCompletedOn] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const presetChips: { key: Preset; label: string }[] = [
    { key: 'adjourned', label: 'Adjourned to...' },
    { key: 'order_reserved', label: 'Order reserved' },
    { key: 'submissions_completed', label: 'Submissions completed' },
    { key: 'order_received', label: 'Order received' },
  ];

  const handleSave = async () => {
    if (preset === 'order_received' && !outcomeText.trim()) {
      showToast('Outcome text required for order received', 'error');
      return;
    }
    setSaving(true);
    try {
      let stepUpdate: any = {};
      let extraActions: any = undefined;

      switch (preset) {
        case 'adjourned':
          stepUpdate = { status: 'ADJOURNED', outcome: outcomeText.trim() || `Adjourned to ${nextDate}`, completedOn: completedOn || null };
          extraActions = {
            createStep: {
              type: 'HEARING' as StepType,
              title: 'Next hearing',
              hearingDate: nextDate || null,
              status: 'PENDING' as StepStatus,
              stage: step.stage,
            },
          };
          break;
        case 'order_reserved':
          stepUpdate = { status: 'COMPLETED', outcome: outcomeText.trim() || 'Order reserved', completedOn: completedOn || null };
          if (pauseAsAwaited) {
            extraActions = { pauseLitigation: { pausedReason: 'ORDER_AWAITED' } };
          }
          break;
        case 'submissions_completed':
          stepUpdate = { status: 'COMPLETED', outcome: outcomeText.trim() || 'Submissions completed', completedOn: completedOn || null };
          break;
        case 'order_received':
          stepUpdate = { status: 'COMPLETED', outcome: outcomeText.trim(), completedOn: completedOn || null };
          break;
        default:
          stepUpdate = { status: 'COMPLETED', outcome: outcomeText.trim() || null, completedOn: completedOn || null };
          break;
      }

      await onSave(stepUpdate, extraActions);
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">Log Outcome</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">{'✕'}</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-gray-500">Step: <b className="text-gray-800">{step.title}</b></div>

          {/* Preset chips */}
          <div className="flex flex-wrap gap-2">
            {presetChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => setPreset(preset === chip.key ? null : chip.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${preset === chip.key ? 'bg-blue-100 text-blue-800 border-blue-300 font-medium' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {/* Conditional fields */}
          {preset === 'adjourned' && (
            <div>
              <label className="label">Next date</label>
              <input type="date" className="input-field" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
            </div>
          )}

          {preset === 'order_reserved' && (
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" checked={pauseAsAwaited} onChange={(e) => setPauseAsAwaited(e.target.checked)} />
              Pause litigation as ORDER_AWAITED?
            </label>
          )}

          {/* Outcome text */}
          <div>
            <label className="label">Outcome / Notes {preset === 'order_received' && <span className="text-red-500">*</span>}</label>
            <input className="input-field" value={outcomeText} onChange={(e) => setOutcomeText(e.target.value)} placeholder="Brief outcome summary" />
          </div>

          {/* Completed on */}
          <div>
            <label className="label">Completed On</label>
            <input type="date" className="input-field" value={completedOn} onChange={(e) => setCompletedOn(e.target.value)} />
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-sm" disabled={saving}>Cancel</button>
          <button type="button" onClick={handleSave} className="btn-primary text-sm" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

// ---- Move Stage modal -------------------------------------------------------

const MoveStageModal: React.FC<{
  litigation: Litigation;
  onClose: () => void;
  onSave: (data: { currentStage: Stage; createAppealStep: boolean; appealDueDate: string | null }) => Promise<void>;
}> = ({ litigation, onClose, onSave }) => {
  const [stage, setStage] = useState<Stage>(litigation.currentStage ?? 'FIRST_APPEAL');
  const [createStep, setCreateStep] = useState(true);
  const [saving, setSaving] = useState(false);

  // Compute pre-filled due date from limitation presets
  const prefilledDate = useMemo(() => {
    if (!litigation.lawArea) return '';
    const presets = LIMITATION_PRESETS[litigation.lawArea];
    if (!presets || !presets[stage]) return '';
    const d = new Date(todayISO());
    d.setDate(d.getDate() + presets[stage]);
    return d.toISOString().slice(0, 10);
  }, [litigation.lawArea, stage]);

  const [dueDate, setDueDate] = useState(prefilledDate);

  // Update due date when stage or prefill changes
  useEffect(() => {
    setDueDate(prefilledDate);
  }, [prefilledDate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ currentStage: stage, createAppealStep: createStep, appealDueDate: dueDate || null });
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-3 border-b bg-gradient-to-r from-purple-50 to-indigo-50 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">Move Stage</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">{'✕'}</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label">New Stage</label>
            <div className="space-y-1.5 mt-1">
              {STAGE_OPTIONS.map((s) => (
                <label key={s} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input type="radio" name="stage" checked={stage === s} onChange={() => setStage(s)} />
                  <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${STAGE_BADGE[s]}`}>{fmtStage(s)}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={createStep} onChange={(e) => setCreateStep(e.target.checked)} />
            Create appeal step?
          </label>

          {createStep && (
            <div>
              <label className="label">Due date</label>
              <input type="date" className="input-field" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              {prefilledDate && <p className="text-[10px] text-gray-400 mt-0.5">Pre-filled from limitation period ({LIMITATION_PRESETS[litigation.lawArea!]?.[stage]} days)</p>}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-sm" disabled={saving}>Cancel</button>
          <button type="button" onClick={handleSave} className="btn-primary text-sm" disabled={saving}>{saving ? 'Saving...' : 'Move'}</button>
        </div>
      </div>
    </div>
  );
};

// ---- Litigation editor modal ------------------------------------------------

const LitigationEditor: React.FC<{
  existing: Litigation | null;
  staff: StaffLite[];
  onClose: () => void;
  onSaved: () => void;
  onSubmit: (data: any) => Promise<void>;
}> = ({ existing, staff, onClose, onSaved, onSubmit }) => {
  const isCreate = !existing;

  // Template (create only)
  const [templateKey, setTemplateKey] = useState('');
  const [noticeDate, setNoticeDate] = useState('');

  const [title, setTitle] = useState(existing?.title ?? '');
  const [caseNumber, setCaseNumber] = useState(existing?.caseNumber ?? '');
  const [forum, setForum] = useState(existing?.forum ?? '');
  const [lawArea, setLawArea] = useState<LawArea | ''>(existing?.lawArea ?? '');
  const [currentStage, setCurrentStage] = useState<Stage | ''>(existing?.currentStage ?? '');
  const [responsibleId, setResponsibleId] = useState<number | ''>(existing?.responsibleId ?? existing?.responsible?.id ?? '');
  const [opposingParty, setOpposingParty] = useState(existing?.opposingParty ?? '');
  const [ourReference, setOurReference] = useState(existing?.ourReference ?? '');
  const [status, setStatus] = useState<LitStatus>(existing?.status ?? 'OPEN');
  const [startedOn, setStartedOn] = useState(existing?.startedOn?.slice(0, 10) ?? (isCreate ? todayISO() : ''));
  const [closedOn, setClosedOn] = useState(existing?.closedOn?.slice(0, 10) ?? '');
  const [limitationDate, setLimitationDate] = useState(existing?.limitationDate?.slice(0, 10) ?? '');
  const [limitationBasis, setLimitationBasis] = useState(existing?.limitationBasis ?? '');
  const [disputedAmount, setDisputedAmount] = useState(existing?.disputedAmount != null ? String(existing.disputedAmount) : '');
  const [preDepositPaid, setPreDepositPaid] = useState(existing?.preDepositPaid != null ? String(existing.preDepositPaid) : '');
  const [preDepositRef, setPreDepositRef] = useState(existing?.preDepositRef ?? '');
  const [outcome, setOutcome] = useState(existing?.outcome ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');

  // Pause fields (edit only)
  const [pausedReason, setPausedReason] = useState<PausedReason | ''>(existing?.pausedReason ?? '');
  const [pausedOn, setPausedOn] = useState(existing?.pausedOn?.slice(0, 10) ?? '');
  const [pausedNote, setPausedNote] = useState(existing?.pausedNote ?? '');
  const [resumeOn, setResumeOn] = useState(existing?.resumeOn?.slice(0, 10) ?? '');

  const [saving, setSaving] = useState(false);

  // Template auto-fill
  const handleTemplateChange = (key: string) => {
    setTemplateKey(key);
    switch (key) {
      case 'GST_SCN_73_74':
        setLawArea('GST');
        setCurrentStage('ASSESSMENT');
        setForum('GST Department');
        break;
      case 'IT_SCRUTINY':
        setLawArea('INCOME_TAX');
        setCurrentStage('ASSESSMENT');
        setForum('Income Tax Department');
        break;
      case 'IT_FIRST_APPEAL':
        setLawArea('INCOME_TAX');
        setCurrentStage('FIRST_APPEAL');
        setForum('CIT(A)');
        break;
      case 'GST_FIRST_APPEAL':
        setLawArea('GST');
        setCurrentStage('FIRST_APPEAL');
        setForum('GST Appellate Authority');
        break;
      default:
        break;
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { showToast('Title required', 'error'); return; }
    setSaving(true);
    try {
      const data: any = {
        title: title.trim(),
        caseNumber: caseNumber.trim() || null,
        forum: forum.trim() || null,
        lawArea: lawArea || null,
        currentStage: currentStage || null,
        responsibleId: responsibleId || null,
        opposingParty: opposingParty.trim() || null,
        ourReference: ourReference.trim() || null,
        status,
        startedOn: startedOn || null,
        closedOn: closedOn || null,
        limitationDate: limitationDate || null,
        limitationBasis: limitationBasis.trim() || null,
        disputedAmount: disputedAmount.trim() === '' ? null : Number(disputedAmount),
        preDepositPaid: preDepositPaid.trim() === '' ? null : Number(preDepositPaid),
        preDepositRef: preDepositRef.trim() || null,
        outcome: outcome.trim() || null,
        notes: notes.trim() || null,
      };

      if (!isCreate) {
        data.pausedReason = pausedReason || null;
        data.pausedOn = pausedOn || null;
        data.pausedNote = pausedNote.trim() || null;
        data.resumeOn = resumeOn || null;
      }

      if (isCreate && templateKey) {
        data.templateKey = templateKey;
        data.noticeDate = noticeDate || null;
      }

      await onSubmit(data);
      showToast(existing ? 'Litigation updated' : 'Litigation created', 'success');
      onSaved();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-3 border-b bg-gradient-to-r from-purple-50 to-indigo-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">{existing ? 'Edit Litigation' : '+ New Litigation'}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">{'✕'}</button>
        </div>
        <div className="p-5 space-y-3">

          {/* Template (create only) */}
          {isCreate && (
            <div>
              <label className="label">Start from template</label>
              <select className="input-field" value={templateKey} onChange={(e) => handleTemplateChange(e.target.value)}>
                {TEMPLATE_OPTIONS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              {templateKey && (
                <div className="mt-2">
                  <label className="label">Notice / Order date</label>
                  <input type="date" className="input-field" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} />
                </div>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="label">Title <span className="text-red-500">*</span></label>
            <input className="input-field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder='e.g. "SCN under Sec 74 -- FY 2022-23"' required />
          </div>

          {/* Case Number + Forum */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Case Number</label>
              <input className="input-field" value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} placeholder="External case ref" />
            </div>
            <div>
              <label className="label">Forum / Authority</label>
              <input className="input-field" value={forum} onChange={(e) => setForum(e.target.value)} placeholder="GST Appellate / CIT(A) / ITAT / HC" />
            </div>
          </div>

          {/* Law Area + Current Stage */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Law Area</label>
              <select className="input-field" value={lawArea} onChange={(e) => setLawArea(e.target.value as LawArea | '')}>
                <option value="">-- Select --</option>
                {LAW_AREA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Current Stage</label>
              <select className="input-field" value={currentStage} onChange={(e) => setCurrentStage(e.target.value as Stage | '')}>
                <option value="">-- Select --</option>
                {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{fmtStage(s)}</option>)}
              </select>
            </div>
          </div>

          {/* Responsible */}
          <div>
            <label className="label">Responsible</label>
            <select className="input-field" value={responsibleId} onChange={(e) => setResponsibleId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">-- Unassigned --</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
            </select>
          </div>

          {/* Opposing Party + Our Reference */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Opposing Party</label>
              <input className="input-field" value={opposingParty} onChange={(e) => setOpposingParty(e.target.value)} placeholder="Department / counterparty" />
            </div>
            <div>
              <label className="label">Our Reference</label>
              <input className="input-field" value={ourReference} onChange={(e) => setOurReference(e.target.value)} placeholder="Internal ref" />
            </div>
          </div>

          {/* Status + Started On + Closed On */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Status</label>
              <select className="input-field" value={status} onChange={(e) => setStatus(e.target.value as LitStatus)}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Started On</label>
              <input type="date" className="input-field" value={startedOn} onChange={(e) => setStartedOn(e.target.value)} />
            </div>
            <div>
              <label className="label">Closed On</label>
              <input type="date" className="input-field" value={closedOn} onChange={(e) => setClosedOn(e.target.value)} />
            </div>
          </div>

          {/* Limitation Date + Basis */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Limitation Date</label>
              <input type="date" className="input-field" value={limitationDate} onChange={(e) => setLimitationDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Limitation Basis</label>
              <input className="input-field" value={limitationBasis} onChange={(e) => setLimitationBasis(e.target.value)} placeholder="e.g. Sec 249(2) — 30 days from order" />
            </div>
          </div>

          {/* Disputed Amount + Pre-deposit + Ref */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Disputed Amount</label>
              <input type="number" step="0.01" min="0" className="input-field" value={disputedAmount} onChange={(e) => setDisputedAmount(e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className="label">Pre-deposit Paid</label>
              <input type="number" step="0.01" min="0" className="input-field" value={preDepositPaid} onChange={(e) => setPreDepositPaid(e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className="label">Pre-deposit Ref</label>
              <input className="input-field" value={preDepositRef} onChange={(e) => setPreDepositRef(e.target.value)} placeholder="Challan / receipt ref" />
            </div>
          </div>

          {/* Outcome + Notes */}
          <div>
            <label className="label">Outcome (final order summary)</label>
            <textarea className="input-field" rows={2} value={outcome} onChange={(e) => setOutcome(e.target.value)} />
          </div>
          <div>
            <label className="label">Internal Notes</label>
            <textarea className="input-field" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {/* Pause fields (edit only) */}
          {!isCreate && (
            <div className="border-t border-gray-200 pt-3 mt-3">
              <h4 className="text-xs font-semibold text-gray-600 mb-2">Pause / Dormancy</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Paused Reason</label>
                  <select className="input-field" value={pausedReason} onChange={(e) => setPausedReason(e.target.value as PausedReason | '')}>
                    <option value="">-- Not paused --</option>
                    {PAUSED_REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Paused On</label>
                  <input type="date" className="input-field" value={pausedOn} onChange={(e) => setPausedOn(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="label">Paused Note</label>
                  <input className="input-field" value={pausedNote} onChange={(e) => setPausedNote(e.target.value)} placeholder="Reason details" />
                </div>
                <div>
                  <label className="label">Resume On</label>
                  <input type="date" className="input-field" value={resumeOn} onChange={(e) => setResumeOn(e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-sm" disabled={saving}>Cancel</button>
          <button type="submit" className="btn-primary text-sm" disabled={saving}>{saving ? 'Saving...' : existing ? 'Save changes' : 'Create'}</button>
        </div>
      </form>
    </div>
  );
};

// ---- Step editor modal ------------------------------------------------------

const StepEditor: React.FC<{
  existing: Step | null;
  staff: StaffLite[];
  defaultStage?: Stage | null;
  defaultAssigneeId?: number | null;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}> = ({ existing, staff, defaultStage, defaultAssigneeId, onClose, onSubmit }) => {
  const [type, setType] = useState<StepType>(existing?.type ?? 'REPLY');
  const [title, setTitle] = useState(existing?.title ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [dueDate, setDueDate] = useState(existing?.dueDate?.slice(0, 10) ?? '');
  const [hearingDate, setHearingDate] = useState(existing?.hearingDate?.slice(0, 10) ?? '');
  const [status, setStatus] = useState<StepStatus>(existing?.status ?? 'PENDING');
  const [completedOn, setCompletedOn] = useState(existing?.completedOn?.slice(0, 10) ?? '');
  const [outcome, setOutcome] = useState(existing?.outcome ?? '');
  const [billableAmount, setBillableAmount] = useState(existing?.billableAmount != null ? String(existing.billableAmount) : '');
  const [assignedToId, setAssignedToId] = useState<number | ''>(existing?.assignedTo?.id ?? (defaultAssigneeId ?? ''));
  const [stage, setStage] = useState<Stage | ''>(existing?.stage ?? (defaultStage ?? ''));
  const [saving, setSaving] = useState(false);
  const [prevStatus, setPrevStatus] = useState<StepStatus>(existing?.status ?? 'PENDING');

  // Auto-fill completedOn when switching to COMPLETED
  const handleStatusChange = (newStatus: StepStatus) => {
    if (newStatus === 'COMPLETED' && prevStatus !== 'COMPLETED' && !completedOn) {
      setCompletedOn(todayISO());
    }
    setPrevStatus(status);
    setStatus(newStatus);
  };

  // Outcome warning
  const showOutcomeWarning = (type === 'HEARING' || type === 'ORDER_RECEIVED') && status === 'COMPLETED' && !outcome.trim();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { showToast('Title required', 'error'); return; }
    setSaving(true);
    try {
      await onSubmit({
        type, title: title.trim(),
        description: description.trim() || null,
        dueDate: dueDate || null,
        hearingDate: hearingDate || null,
        status,
        completedOn: completedOn || null,
        outcome: outcome.trim() || null,
        billableAmount: billableAmount.trim() === '' ? null : Number(billableAmount),
        assignedToId: assignedToId || null,
        stage: stage || null,
      });
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-3 border-b bg-gradient-to-r from-indigo-50 to-blue-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">{existing ? 'Edit Step' : '+ Add Step'}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">{'✕'}</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Type</label>
              <select className="input-field" value={type} onChange={(e) => setType(e.target.value as StepType)}>
                {STEP_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              <select className="input-field" value={status} onChange={(e) => handleStatusChange(e.target.value as StepStatus)}>
                {STEP_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Stage</label>
              <select className="input-field" value={stage} onChange={(e) => setStage(e.target.value as Stage | '')}>
                <option value="">-- None --</option>
                {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{fmtStage(s)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Title <span className="text-red-500">*</span></label>
            <input className="input-field" value={title} onChange={(e) => setTitle(e.target.value)} required
              placeholder={type === 'HEARING' ? 'e.g. Personal hearing 1' : type === 'REPLY' ? 'e.g. Reply to SCN dated 12-May' : type === 'APPEAL' ? 'e.g. Appeal to CIT(A)' : type === 'ORDER_RECEIVED' ? 'e.g. Order u/s 73' : type === 'NOTICE_RECEIVED' ? 'e.g. SCN u/s 73' : ''}
            />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input-field" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Due Date</label>
              <input type="date" className="input-field" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Hearing Date</label>
              <input type="date" className="input-field" value={hearingDate} onChange={(e) => setHearingDate(e.target.value)} disabled={type !== 'HEARING'} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Completed On</label>
              <input type="date" className="input-field" value={completedOn} onChange={(e) => setCompletedOn(e.target.value)} />
            </div>
            <div>
              <label className="label">Billable Amount ({'₹'})</label>
              <input type="number" step="0.01" min="0" className="input-field" value={billableAmount} onChange={(e) => setBillableAmount(e.target.value)} placeholder="0" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Assigned To</label>
              <select className="input-field" value={assignedToId} onChange={(e) => setAssignedToId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">-- Unassigned --</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Outcome / Notes</label>
              <input className="input-field" value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="e.g. Adjourned to 25-Jun" />
              {showOutcomeWarning && (
                <p className="text-[10px] text-amber-600 mt-0.5">Outcome is recommended for completed hearings/orders</p>
              )}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-sm" disabled={saving}>Cancel</button>
          <button type="submit" className="btn-primary text-sm" disabled={saving}>{saving ? 'Saving...' : existing ? 'Save changes' : 'Add step'}</button>
        </div>
      </form>
    </div>
  );
};

export default LitigationsPanel;
